import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createConsultationAsync, clearError } from '../store/slices/consultationsSlice';
import { useLevels } from '../hooks/useLevels';
import { fetchAdsAsync } from '../store/slices/adsSlice';
import LoadingSpinner from './LoadingSpinner';
import Select, { SelectOption } from './Select';
import ConsultationLostReasonField from './ConsultationLostReasonField';
import StudentReferrerPicker, { type ReferrerOption } from './StudentReferrerPicker';
import {
  STATUSES,
  STATUS_LABELS,
  type ConsultationStatus,
} from '../constants/consultationStatus';
import type { Consultation } from '../store/slices/consultationsSlice';
import './GroupForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// The consultation lang_level DB enum — deliberately not coupled to useLevels()
// (that's the Group level enum, loaded async).
const LANG_LEVEL_VALUES = ['a0', 'a1', 'a1+', 'a2', 'a2+', 'b1', 'b1+', 'b2', 'c1', 'c2'];

const CreateConsultation: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error } = useAppSelector((state) => state.consultations);
  const { ads } = useAppSelector((state) => state.ads);
  const { token } = useAppSelector((state) => state.auth);
  const levels = useLevels();

  const fromContactId = searchParams.get('fromContact');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    patronymic: '',
    contact: '',
    firstContactDate: '',
    consultationDate: '',
    consultationTime: '',
    adId: '',
    isOrganic: false,
    status: 'scheduled',
    langLevel: '',
    commentary: '',
    goal: '',
    profession: '',
    lostReason: '',
    lostReasonOther: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [referrerOption, setReferrerOption] = useState<ReferrerOption | null>(null);

  // Load ads for the dropdown
  useEffect(() => {
    dispatch(fetchAdsAsync({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Prefill from the source contact when scheduling via /contacts
  useEffect(() => {
    if (!fromContactId || !token) return;
    axios.get(`${API_BASE_URL}/contacts/${fromContactId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const c = res.data.contact;
      const nameParts = (c.name || '').trim().split(/\s+/).filter(Boolean);
      const level = (c.level || '').toLowerCase();
      setFormData(prev => ({
        ...prev,
        firstName: nameParts[0] || prev.firstName,
        lastName: nameParts.slice(1).join(' ') || prev.lastName,
        contact: c.phone || prev.contact,
        langLevel: LANG_LEVEL_VALUES.includes(level) ? level : prev.langLevel,
        firstContactDate: c.created_at ? c.created_at.slice(0, 10) : prev.firstContactDate,
      }));
    }).catch(() => {});
  }, [fromContactId, token]);

  // Filter active ads (memoized to avoid unnecessary recalculations)
  const activeAds = useMemo(() => {
    return ads.filter(ad => {
      const endDate = new Date(ad.dateEnd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return endDate >= today;
    });
  }, [ads]);

  // Separate expired ads
  const expiredAds = useMemo(() => {
    return ads.filter(ad => {
      const endDate = new Date(ad.dateEnd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return endDate < today;
    });
  }, [ads]);

  // Any status is allowed on create (the row starts here).
  const statusOptions = useMemo<SelectOption<ConsultationStatus>[]>(
    () => STATUSES.map(s => ({ value: s, label: STATUS_LABELS[s] })),
    []
  );
  const selectedStatus = statusOptions.find(o => o.value === formData.status) || null;
  const isReferralSource = referrerOption !== null;

  // Preselect if only one active ad. Derived during render so it stays in sync
  // with async-loaded ads without a setState-in-effect; the user can still
  // override it via the select (which sets formData.adId directly).
  const effectiveAdId = isReferralSource ? '' :
    formData.adId ||
    (!formData.isOrganic && activeAds.length === 1 ? activeAds[0].id.toString() : '');

  const handleReferrerChange = (option: ReferrerOption | null) => {
    setReferrerOption(option);
    if (option) {
      setFormData(prev => ({ ...prev, isOrganic: false, adId: '' }));
      setFormErrors(prev => ({ ...prev, adId: '' }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked,
        // Clear adId when marking as organic
        ...(name === 'isOrganic' && checked ? { adId: '' } : {})
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear form error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.consultationDate) {
      errors.consultationDate = 'Consultation date is required';
    }

    if (!isReferralSource && !formData.isOrganic && !effectiveAdId) {
      errors.adId = 'Ad is required for non-organic consultations';
    }

    if (formData.status === 'not_interested' && formData.lostReason === 'other' && !formData.lostReasonOther.trim()) {
      errors.lostReasonOther = 'A note is required when the reason is "Other"';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    if (error) dispatch(clearError());
    
    if (!validateForm()) {
      return;
    }

    const consultationData: Partial<Consultation> & {
      goal?: string | null;
      profession?: string | null;
    } = {
      firstName: formData.firstName.trim() || null,
      lastName: formData.lastName.trim() || null,
      patronymic: formData.patronymic.trim() || null,
      contact: formData.contact.trim() || null,
      firstContactDate: formData.firstContactDate || null,
      consultationDate: formData.consultationDate,
      consultationTime: formData.consultationTime || null,
      isOrganic: isReferralSource ? false : formData.isOrganic,
      status: (formData.status as ConsultationStatus) || null,
      langLevel: formData.langLevel || null,
      commentary: formData.commentary.trim() || null,
      goal: formData.goal.trim() || null,
      profession: formData.profession.trim() || null,
      lostReason: formData.status === 'not_interested' ? (formData.lostReason || null) : null,
      lostReasonOther: formData.status === 'not_interested' ? (formData.lostReasonOther.trim() || null) : null,
      referrerStudentId: referrerOption ? referrerOption.value : null,
    };

    if (fromContactId) {
      consultationData.contactId = parseInt(fromContactId, 10);
    }

    // Only include adId if not organic
    if (isReferralSource || formData.isOrganic) {
      consultationData.adId = null;
    } else {
      consultationData.adId = effectiveAdId ? parseInt(effectiveAdId, 10) : null;
    }

    try {
      await dispatch(createConsultationAsync(consultationData)).unwrap();
      // Navigate back to consultations list
      navigate('/consultations');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleCancel = () => {
    navigate('/consultations');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Create New Consultation</h1>
          <p>Add a new consultation record</p>
        </div>
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Client Information</h2>

          <div className="form-row-compact three-columns">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter first name"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter last name"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="patronymic">Patronymic</label>
              <input
                type="text"
                id="patronymic"
                name="patronymic"
                value={formData.patronymic}
                onChange={handleInputChange}
                placeholder="Enter patronymic"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact">Contact</label>
            <input
              type="text"
              id="contact"
              name="contact"
              value={formData.contact}
              onChange={handleInputChange}
              placeholder="Phone, email, or other contact"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="commentary">Note</label>
            <textarea
              id="commentary"
              name="commentary"
              value={formData.commentary}
              onChange={handleInputChange}
              placeholder="Additional notes or comments"
              className="form-input"
              rows={3}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Acquisition Source</h2>

          <div className="form-group">
            <label>Brought by</label>
            <StudentReferrerPicker
              value={referrerOption}
              onChange={handleReferrerChange}
            />
          </div>

          {isReferralSource ? (
            <div className="form-group">
              <label>Source</label>
              <div className="consultation-source-locked">
                <strong>Referral</strong>
                <span>Locked while “Brought by” is set</span>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="checkbox-control">
                <input
                  type="checkbox"
                  name="isOrganic"
                  checked={formData.isOrganic}
                  onChange={handleInputChange}
                />
                <span className="checkbox-control__label">Organic (no ad source)</span>
              </label>
            </div>
          )}

          {!isReferralSource && !formData.isOrganic && (
            <div className="form-group">
              <label htmlFor="adId">Ad *</label>
              <select
                id="adId"
                name="adId"
                value={effectiveAdId}
                onChange={handleInputChange}
                className={`form-select ${formErrors.adId ? 'error' : ''}`}
                required={!formData.isOrganic}
              >
                {(activeAds.length > 1 || activeAds.length === 0) && <option value="">Select ad</option>}
                {activeAds.length > 0 && (
                  <optgroup label="Active Ads">
                    {activeAds.map(ad => (
                      <option key={ad.id} value={ad.id}>
                        {ad.name} ({ad.platform})
                      </option>
                    ))}
                  </optgroup>
                )}
                {expiredAds.length > 0 && (
                  <optgroup label="Expired Ads">
                    {expiredAds.map(ad => (
                      <option key={ad.id} value={ad.id}>
                        {ad.name} ({ad.platform})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {formErrors.adId && <span className="error-text">{formErrors.adId}</span>}
            </div>
          )}
        </div>

        <div className="form-section">
          <h2>Consultation Details</h2>

          <div className="form-row-compact">
            <div className="form-group">
              <label htmlFor="firstContactDate">First Contact Date</label>
              <input
                type="date"
                id="firstContactDate"
                name="firstContactDate"
                value={formData.firstContactDate}
                onChange={handleInputChange}
                className={`form-input ${formErrors.firstContactDate ? 'error' : ''}`}
              />
              {formErrors.firstContactDate && <span className="error-text">{formErrors.firstContactDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="consultationDate">Consultation Date *</label>
              <input
                type="date"
                id="consultationDate"
                name="consultationDate"
                value={formData.consultationDate}
                onChange={handleInputChange}
                className={`form-input ${formErrors.consultationDate ? 'error' : ''}`}
                required
              />
              {formErrors.consultationDate && <span className="error-text">{formErrors.consultationDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="consultationTime">Consultation Time</label>
              <input
                type="time"
                id="consultationTime"
                name="consultationTime"
                value={formData.consultationTime}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row-compact">
            <div className="form-group">
              <label>Status *</label>
              <Select<ConsultationStatus, SelectOption<ConsultationStatus>>
                value={selectedStatus}
                onChange={(opt) => setFormData(prev => ({
                  ...prev,
                  status: opt ? opt.value : 'scheduled',
                  ...(opt?.value === 'not_interested' ? {} : { lostReason: '', lostReasonOther: '' }),
                }))}
                options={statusOptions}
                placeholder="Select status…"
                isClearable={false}
              />
            </div>

            <div className="form-group">
              <label htmlFor="langLevel">Language Level</label>
              <select
                id="langLevel"
                name="langLevel"
                value={formData.langLevel}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Not set</option>
                {levels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>

          {formData.status === 'not_interested' && (
            <ConsultationLostReasonField
              lostReason={formData.lostReason}
              lostReasonOther={formData.lostReasonOther}
              noteError={formErrors.lostReasonOther}
              onChange={({ lostReason, lostReasonOther }) => {
                setFormData(prev => ({ ...prev, lostReason, lostReasonOther }));
                if (formErrors.lostReasonOther) setFormErrors(prev => ({ ...prev, lostReasonOther: '' }));
              }}
            />
          )}

          <div className="form-row-compact">
            <div className="form-group">
              <label htmlFor="goal">Goal</label>
              <input
                type="text"
                id="goal"
                name="goal"
                value={formData.goal}
                onChange={handleInputChange}
                placeholder="Student's learning goal"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="profession">Profession</label>
              <input
                type="text"
                id="profession"
                name="profession"
                value={formData.profession}
                onChange={handleInputChange}
                placeholder="Student's profession"
                className="form-input"
              />
            </div>
          </div>

        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-success"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Consultation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateConsultation;
